import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** Phones where the bar is offered before shoppers reach the main button. */
  #phoneQuery = window.matchMedia?.('(max-width: 749px)') ?? null;
  #desktopQuery = window.matchMedia?.('(min-width: 990px)') ?? null;

  #addingFromSticky = false;

  #detailsResizeObserver = null;

  // Measure the actual options column, including centred/narrow page layouts.
  #alignWithDetails = () => {
    if (this.dataset.cpEarlyDesktop !== 'true') return;
    const details = this.closest('.shopify-section')?.querySelector('.product-details');
    if (!details) return;
    const { left, width } = details.getBoundingClientRect();
    this.style.setProperty('--cp-buy-left', `${left + 4}px`);
    this.style.setProperty('--cp-buy-width', `${Math.max(0, width - 8)}px`);
  };

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener('purchase:state', this.#handlePurchaseState, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#phoneQuery?.addEventListener('change', this.#refreshPlacement, { signal });
    this.#desktopQuery?.addEventListener('change', this.#refreshPlacement, { signal });

    target?.addEventListener('input', this.#syncDetails, { signal });
    target?.addEventListener('change', this.#syncDetails, { signal });
    this.#getInitialQuantity();
    this.#syncDetails();
    if (this.dataset.cpEarlyDesktop === 'true') {
      this.#alignWithDetails();
      window.addEventListener('resize', this.#alignWithDetails, { signal, passive: true });
      const details = target?.querySelector('.product-details');
      if (details && typeof ResizeObserver !== 'undefined') {
        this.#detailsResizeObserver?.disconnect();
        this.#detailsResizeObserver = new ResizeObserver(this.#alignWithDetails);
        this.#detailsResizeObserver.observe(details);
        const grid = details.closest('.product-information__grid');
        if (grid) this.#detailsResizeObserver.observe(grid);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#detailsResizeObserver?.disconnect();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.querySelector('[ref="addToCartButton"]');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');


    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      this.#placeStickyBar(entry.isIntersecting, entry.target.getBoundingClientRect());
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '0px 0px 100px 0px',
      }
    );

    this.#buyButtonsBlock = buyButtonsBlock;
    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    if (footer) this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Desktop shows the bar once the main buy buttons have been scrolled past.
   * On phones (print, magnet and card pages) it also shows before shoppers reach them,
   * because the main button sits two or three screens down there.
   * @param {boolean} intersecting - Whether the main buy buttons are on screen
   * @param {DOMRect} rect - The main buy buttons' position
   */
  #placeStickyBar(intersecting, rect) {
    if (intersecting) {
      if (this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
      return;
    }
    if (rect.bottom < 0 || rect.top < 0) {
      this.#showStickyBar();
    } else if (this.#showsAhead()) {
      this.#showStickyBar(true);
    } else if (this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  #showsAhead() {
    if (this.dataset.cpEarlyPhone !== 'true') return false;
    // Print pages on desktop also place the main button below the first screen. Tablets are left
    // out: there the button is close to the first screen and the bar would cover the choices.
    return Boolean(this.#phoneQuery?.matches)
      || (this.dataset.cpEarlyDesktop === 'true' && Boolean(this.#desktopQuery?.matches));
  }

  #refreshPlacement = () => {
    const block = this.#buyButtonsBlock;
    if (!block?.isConnected) return;
    const rect = block.getBoundingClientRect();
    this.#placeStickyBar(rect.bottom > 0 && rect.top < window.innerHeight, rect);
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled) return;
    const incomplete = this.#firstIncompleteField();
    if (incomplete) {
      incomplete.scrollIntoView({ block: 'center', behavior: 'instant' });
      incomplete.focus({ preventScroll: true });
      incomplete.reportValidity?.();
      return;
    }
    this.#addingFromSticky = true;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    if (this.#getProductForm()?.dataset.purchaseState !== 'adding') {
      this.#addingFromSticky = false;
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #showSuccess = async () => {
    const button = this.refs.addToCartButton;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    button.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    const animatedElements = [button];
    if (cartIcon && this.refs.productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      animatedElements.push(flyToCartElement);
    }
    await onAnimationEnd(animatedElements);
    this.#resetTimeout = setTimeout(() => button.removeAttribute('data-added'), 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // The new HTML must not re-enable a button while a cart request is still pending.
    const button = this.#targetAddToCartButton;
    if (button) {
      this.#handlePurchaseState({ detail: {
        productId: this.dataset.productId, blocked: button.disabled,
        busy: button.hasAttribute('aria-busy'),
        label: button.querySelector('.add-to-cart-text__content')?.textContent,
      } });
    }
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  #handlePurchaseState = (event) => {
    if (event.detail?.productId !== this.dataset.productId) return;
    const { busy, blocked, label } = event.detail;
    const button = this.refs.addToCartButton;
    button.disabled = blocked;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    const text = button.querySelector('.add-to-cart-text__content > span:first-child');
    if (text) {
      if (busy) {
        text.dataset.purchaseOriginalLabel ??= text.textContent;
        text.textContent = label;
      } else if (text.dataset.purchaseOriginalLabel !== undefined) {
        text.textContent = text.dataset.purchaseOriginalLabel;
        delete text.dataset.purchaseOriginalLabel;
      }
    }
    this.#updateButtonText();
    this.#syncDetails();
  };

  /** Success feedback belongs only to this form's confirmed addition. */
  #handleCartAddComplete = (event) => {
    const productForm = this.#getProductForm();
    if (event.target !== productForm) return;
    if (this.#addingFromSticky) {
      const confirmed = event.type === ThemeEvents.cartUpdate && !event.detail?.data?.didError;
      const variantId = productForm?.querySelector('input[name="id"]')?.value;
      if (confirmed && productForm?.variantSelectionReady && String(event.detail.sourceId) === String(variantId)) {
        this.#showSuccess();
      } else if (!confirmed) {
        productForm?.querySelector('[ref="addToCartTextError"]')?.scrollIntoView({ block: 'center' });
      }
    }
    this.#addingFromSticky = false;
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine || !this.#getProductForm()?.contains(event.target)) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   * @param {boolean} [ahead] - Shown before the main buy buttons have been reached
   */
  #showStickyBar(ahead = false) {
    if (this.#hiddenByBottom) return;
    this.#syncDetails();
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.cpAhead = String(ahead);
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #firstIncompleteField() {
    const form = this.#getProductForm()?.querySelector('form');
    return [...(form?.elements || [])].find((field) =>
      field.willValidate && !field.validity.valid && field.getClientRects().length
    );
  }

  #syncDetails = () => {
    const primary = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (!primary) return;
    this.#targetAddToCartButton = primary;
    const button = this.refs.addToCartButton;
    const label = button.querySelector('.add-to-cart-text__content > span:first-child');
    const incomplete = !primary.disabled && this.#firstIncompleteField();
    const primaryLabel = primary.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    const action = incomplete ? (incomplete.name?.startsWith('properties[') ? 'Complete details' : 'Choose options') : primaryLabel;
    button.disabled = primary.disabled;
    if (label && action) label.textContent = action;
    if (action) button.setAttribute('aria-label', action);
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
